import { inject, Injectable } from '@angular/core';
import { ProviderName } from '../../../../account/models/login-response.model';
import { LoggingService } from '../../core/logging.service';
import { ExpressCheckoutService } from './express-checkout.service';
import { ExpressCheckoutContext } from '../../../models/orders/express-checkout.token';

/**
 * FreedomPay Hosted Payment Component (HPC) service. Loads the HPC SDK script, starts an express
 * checkout session (via api/cardtokens/express/init) that returns the hosted iframe markup, and
 * completes the card token save (via api/cardtokens/express/complete) with the captured SessionKeys.
 */
@Injectable({
  providedIn: 'root',
})
export class FreedomPayService {
  private logService = inject(LoggingService);
  private expressCheckoutService = inject(ExpressCheckoutService);
  private initialized = false;

  /* ================================
   * Public API
   * ================================ */

  async initialize(options: { settings: any }): Promise<boolean> {
    if (this.initialized) {
      return true;
    }
    try {
      // The base already ran init and passed the resolved action in — use it (do not re-fetch).
      const { settings } = options;
      // FreedomPay returns the iframe markup in `Url`; fall back to `Form` for other providers.
      const iframeMarkup = settings?.iframeHtml || '';
      const sessionKey = settings?.sessionKey || '';
      if (!iframeMarkup) {
        this.logService.error('FreedomPay express init response missing iframe markup');
        return false;
      }

      if (!sessionKey) {
        this.logService.error('FreedomPay express init response missing sessionKey');
        return false;
      }
      await this.loadSdk(iframeMarkup);

      this.initialized = true;
      return true;
    } catch (error) {
      this.logService.error('FreedomPay startSession failed:', error);
      return false;
    }
  }

  /**
   * Load the HPC SDK onto the parent page and report whether it registered itself. Never throws and
   * never blocks initialize(): a missing or blocked SDK only costs Apple Pay, so it is logged and
   * the provider still renders for the in-frame flows.
   */
  private async loadSdk(iframeMarkup: string): Promise<boolean> {
    const sdkUrl = this.deriveSdkUrlFromIframe(iframeMarkup);
    if (!sdkUrl) {
      this.logService.error('FreedomPay: no HPC SDK script url resolved; Apple Pay cannot be driven from the parent page');
      return false;
    }

    try {
      await this.expressCheckoutService.loadScript(sdkUrl);
    } catch (error) {
      // A blocked/unreachable script (CSP, offline, 404) must not take the provider down with it.
      this.logService.error('FreedomPay: HPC SDK script failed to load; Apple Pay will not be available', error);
      return false;
    }

    // The SDK exposes window.FreedomPay.Apm and binds its own message listener as it evaluates.
    if (!(window as any).FreedomPay?.Apm) {
      this.logService.error('FreedomPay: HPC SDK loaded but window.FreedomPay.Apm is missing; Apple Pay will not be available');
      return false;
    }

    return true;
  }

  /**
   * Derive the SDK url from the hosted iframe url, which is the only place the HPC host and API
   * version reach the client (the API returns no script url for this call). The iframe src is
   * `https://{host}/api/v{version}/controls?sessionKey=...` and the SDK is a sibling of `controls`
   * on the same host and version: `https://{host}/api/v{version}/cdn/hpc_min.js`.
   *
   * Deriving rather than hardcoding keeps UAT/production correct automatically, since the host is
   * inherited from whichever environment the API pointed the iframe at. The result is executed as a
   * script on our own page, so it is constrained to https on a FreedomPay host — a tampered init
   * response must not be able to source JavaScript from anywhere it likes.
   */
  private deriveSdkUrlFromIframe(iframeMarkup: string): string | null {
    // Match the src of the iframe markup, then the /api/v{version}/ segment within it.
    const src = /<iframe[^>]*\ssrc=["']([^"']+)["']/i.exec(iframeMarkup)?.[1];
    if (!src) {
      return null;
    }

    try {
      const url = new URL(src);
      if (url.protocol !== 'https:' || !/(^|\.)freedompay\.com$/i.test(url.hostname)) {
        this.logService.error('FreedomPay: refusing to load the HPC SDK from an unexpected origin:', url.origin);
        return null;
      }

      const apiVersion = /\/api\/(v[\d.]+)\//i.exec(url.pathname)?.[1];
      if (!apiVersion) {
        return null;
      }
      return `${url.origin}/api/${apiVersion}/cdn/hpc_min.js`;
    } catch {
      return null;
    }
  }

  /**
   * Complete the express checkout with the captured SessionKeys (from the iframe). Routes to the
   * card-token or marketplace complete endpoint by `context.mode` via `completeExpress`. Resolves to
   * true on success.
   */
  async submitSessionKeys(sessionKeys: string, context: ExpressCheckoutContext, sessionKey: string): Promise<boolean> {
    try {
      const response: any = await this.expressCheckoutService.completeExpress(context, ProviderName.FreedomPay, {
        responseString: sessionKeys,
        queryString: sessionKey,
      });
      return !!(response?.Success ?? response?.success);
    } catch (error) {
      this.logService.error('FreedomPay submitSessionKeys failed:', error);
      return false;
    }
  }
}
